import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { formatDateTime } from '../../../lib/i18n-format';
import { actorsOf, portalAccessRows, type PortalAccessEvent } from './portal-access.model';

// ─── «Accesos al portal» en la ficha de la persona ──────────────────────────
// Quién abrió su portal con «Ver portal», con qué rol y cuándo; lo más reciente
// primero. Lo ven tenantAdmin y registro (las reglas ya les dejan leer
// auditEvents). El nombre sale de la ficha de quien abrió (`people.authUid`):
// la plataforma no tiene ficha en el seminario y se muestra como tal.
//
// LA CONSULTA TRAE SOLO APERTURAS (filtro por `action` en el servidor, índice
// targetCollection + targetId + action + occurredAt desc). Filtrarlas en el
// cliente sobre «los últimos N eventos de la persona» perdía aperturas en
// cuanto la persona acumulaba otros eventos. De a PAGE, con el total.

const PAGE = 20;

export function PortalAccessCard({
  tenantId,
  personId,
}: {
  tenantId: string;
  personId: string;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [shown, setShown] = useState(PAGE);

  const opened = () =>
    query(
      collection(db, `tenants/${tenantId}/auditEvents`),
      where('targetCollection', '==', 'people'),
      where('targetId', '==', personId),
      where('action', '==', 'portal_view.opened'),
    );

  const q = useQuery({
    queryKey: ['portal-access', tenantId, personId, shown],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const [snap, count] = await Promise.all([
        getDocs(query(opened(), orderBy('occurredAt', 'desc'), limit(shown))),
        getCountFromServer(opened()),
      ]);
      const events: PortalAccessEvent[] = snap.docs.map((d) => {
        const x = d.data();
        return {
          actor: String(x['actor'] ?? ''),
          actorRole: (x['actorRole'] as string | null | undefined) ?? null,
          action: String(x['action'] ?? ''),
          occurredAt: (x['occurredAt'] as { toDate?: () => Date })?.toDate?.() ?? new Date(0),
        };
      });
      const names = new Map<string, string>();
      const uids = actorsOf(events);
      for (let i = 0; i < uids.length; i += 30) {
        const people = await getDocs(
          query(
            collection(db, `tenants/${tenantId}/people`),
            where('authUid', 'in', uids.slice(i, i + 30)),
          ),
        );
        for (const p of people.docs) {
          const d = p.data();
          const name = [d['firstName'], d['lastName']].filter(Boolean).join(' ').trim();
          if (d['authUid'] && name) names.set(String(d['authUid']), name);
        }
      }
      return { rows: portalAccessRows(events, names, shown), total: count.data().count };
    },
    staleTime: 30_000,
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="600">
          {t('people.portalAccess.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('people.portalAccess.help')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        {q.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            {t('people.portalAccess.loading')}
          </Typography>
        ) : q.isError ? (
          <Typography variant="body2" color="error">
            {t('people.portalAccess.error')}
          </Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('people.portalAccess.none')}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">
              {t('people.portalAccess.showing', { shown: rows.length, count: total })}
            </Typography>
            <List dense disablePadding>
              {rows.map((row) => (
                <ListItem key={row.key} disableGutters>
                  <ListItemText
                    primary={
                      row.role === 'platformAdmin'
                        ? t('people.portalAccess.platform')
                        : (row.who ?? t('people.portalAccess.noRecord'))
                    }
                    secondary={`${t(`people.portalAccess.roles.${row.role}`)} · ${formatDateTime(
                      row.at,
                      { dateStyle: 'medium', timeStyle: 'short' },
                    )}`}
                  />
                </ListItem>
              ))}
            </List>
            {rows.length < total && (
              <Button
                size="small"
                onClick={() => setShown((n) => n + PAGE)}
                disabled={q.isFetching}
                sx={{ textTransform: 'none', mt: 1 }}
              >
                {t('people.portalAccess.more')}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
